import { useMemo, useState, useEffect } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { Save } from 'lucide-react';
import Papa from 'papaparse';
import { useQueryClient } from 'react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useCampaigns from '@/hooks/useCampaigns';
import axiosFileUpload from '@/utils/axiosFileUpload';

const CsvImporter = () => {
  const modal = useModal('contact-form-drawer');
  const queryClient = useQueryClient();
  const campaign_model = useCampaigns();
  const campaigns = useMemo(() => campaign_model.data || [], [campaign_model.data]);

  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<{ [key: string]: string }>({});
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>('');

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const csv = reader.result as string;
        const parsed = Papa.parse(csv, { header: true, preview: 1 });
        if (parsed.data.length > 0) {
          const fileHeaders = Object.keys(parsed.data[0] as object);
          setHeaders(fileHeaders);

          const initialMapping: { [key: string]: string } = {};
          fileHeaders.forEach((header) => {
            initialMapping[header] = '';
          });
          setColumnMapping(initialMapping);
        }
      };
      reader.readAsText(file);
    } else {
      setHeaders([]);
      setColumnMapping({});
    }
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleCampaignChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
    setSelectedCampaignIds(selectedOptions);
  };

  const handleMappingChange = (csvHeader: string, appField: string) => {
    setColumnMapping((prev) => ({ ...prev, [csvHeader]: appField }));
  };

  const handleImport = async () => {
    if (!file) {
      setStatusMessage('Please select a CSV file to import.');
      return;
    }

    if (selectedCampaignIds.length === 0) {
      setStatusMessage('Please select at least one campaign.');
      return;
    }

    const mappedFields = Object.values(columnMapping).filter((field) => field);
    if (mappedFields.length === 0) {
      setStatusMessage('Please map at least one CSV column to a contact field.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    selectedCampaignIds.forEach((id) => formData.append('campaign_ids[]', id));
    Object.entries(columnMapping).forEach(([csvHeader, appField]) => {
      if (appField) {
        formData.append(`mappings[${csvHeader}]`, appField);
      }
    });

    setIsImporting(true);
    setStatusMessage('Importing contacts...');

    try {
      const response = await axiosFileUpload.post('/v1/contacts/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 202) {
        setStatusMessage('Import started successfully.');
        queryClient.invalidateQueries('v1/contacts');
        modal.hide();
      } else {
        setStatusMessage('Unexpected response from the server.');
      }
    } catch (error: any) {
      console.error('Error importing contacts:', error);
      setStatusMessage('Failed to start import. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Label>Select Campaigns</Label>
        <select
          multiple
          value={selectedCampaignIds}
          onChange={handleCampaignChange}
          className="w-full p-2 border rounded"
        >
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <Label>Upload CSV File</Label>
        <Input type="file" accept=".csv" onChange={handleFileChange} />
      </div>

      {headers.length > 0 && (
        <div>
          <h3 className="mb-4 text-lg font-medium">
            Map CSV Columns to Contact Fields (all other columns will be metadata)
          </h3>
          {headers.map((header) => (
            <div key={header} className="flex items-center my-2">
              <span className="w-1/3">{header}</span>
              <select
                value={columnMapping[header] || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleMappingChange(header, e.target.value)}
                className="w-2/3 p-2 border rounded"
              >
                <option value="">-- Select Field --</option>
                <option value="phone">Phone</option>
                <option value="name">Name</option>
              </select>
            </div>
          ))}

          <div className="mt-6">
            <p className="mb-2">
              <strong>Selected Campaigns:</strong> {selectedCampaignIds.length}
            </p>
          </div>

          {statusMessage && <div className="mb-4 text-sm text-gray-700">{statusMessage}</div>}

          <Button onClick={handleImport} disabled={isImporting} className="mt-4">
            <Save />
            {isImporting ? 'Importing...' : 'Import Contacts'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CsvImporter;
